// GA4 / GTM dataLayer helpers (WebFX tracking restore, 2026-08-31).
// The GTM containers are loaded from public/index.html; everything here just
// pushes events into window.dataLayer per WebFX's spec (Data Layer events
// only — no URL triggers). Every push is wrapped so analytics can never
// break the app.

export type Ga4Item = {
  item_id: string;
  item_name: string;
  item_category: string;
  item_category2: string; // size
  item_category3: string; // paper / media / finish
  item_category4: string; // frame / treatment
  price: number;
  quantity: number;
};

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

function push(event: Record<string, unknown>) {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(event);
  } catch {
    // analytics must never throw into the app
  }
}

/** SPA virtual pageview — fired on every router location change. */
export function pushPageView(path: string) {
  push({
    event: "page_view",
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
}

const PURCHASE_SENT_PREFIX = "ga4_purchase_sent:";

/** True if a purchase event was already pushed for this Stripe session id
 *  in this browser (guards refresh / back-button double fires; GA4 also
 *  dedupes on transaction_id server-side). */
export function purchaseAlreadySent(sessionId: string): boolean {
  try {
    return localStorage.getItem(PURCHASE_SENT_PREFIX + sessionId) === "1";
  } catch {
    return false;
  }
}

/** GA4 e-commerce purchase event. transaction_id is the Stripe checkout
 *  session id (the internal order number is created asynchronously by the
 *  Stripe webhook and may not exist yet when the success page loads). */
export function pushPurchase(params: {
  sessionId: string;
  value: number;
  currency: string;
  items: Ga4Item[];
}) {
  if (purchaseAlreadySent(params.sessionId)) return;
  // WebFX spec: clear any previous ecommerce object before the new push.
  push({ ecommerce: null });
  push({
    event: "purchase",
    ecommerce: {
      transaction_id: params.sessionId,
      value: params.value,
      currency: params.currency,
      items: params.items,
    },
  });
  try {
    localStorage.setItem(PURCHASE_SENT_PREFIX + params.sessionId, "1");
  } catch {
    // storage unavailable — GA4's own transaction_id dedupe still applies
  }
}
